/**
 * The point ledger: points granted, points spent, and nothing else.
 *
 * A character earns two currencies by levelling — stat points into the four
 * attributes `stats.ts` names, and skill points into skills that do not exist
 * yet — and they are the same arithmetic twice. This module is that arithmetic,
 * once, generic over what a point is spent *on*.
 *
 * ## Why this is its own module, and imports nothing
 *
 * It is the seam the skills layer lands on. Ninety skills across three trees
 * are coming (`docs/GAME-DESIGN.md`), and the way this project keeps a fan-out
 * from turning into a rewrite is that the module which owns a concept declares
 * the narrowest interface the other side needs — `inventory.ts`'s `Placeable`,
 * `belt.ts`'s `Quaffable`. Here the narrow thing is the *key*: a `PointLedger`
 * is generic over `TKey extends string` and never learns what a key means, so
 * `PointLedger<AttributeName>` and a ledger over ninety skill ids are the same
 * code with nothing added. A skills module can build its own ledger, refuse its
 * own keys and gate its own prerequisites without this file changing a line —
 * and without importing `progression.ts`, which is where levels are earned.
 *
 * ## What it enforces, and what it deliberately does not
 *
 * It enforces **arithmetic and identity**: you cannot spend a point you have
 * not been granted, cannot spend a fraction of one, cannot spend on a key the
 * ledger does not hold, and cannot un-spend one except through `refundPoints`.
 * That is the whole contract, and `granted` never moves when points are spent
 * or refunded — which is exactly what makes a refund provably exact rather than
 * a second grant wearing a different name.
 *
 * It does **not** enforce content rules — a skill's character-level gate, its
 * prerequisites, D2's cap of 20 hard points in one skill. Those belong to the
 * layer that owns the content, and they sit in *front* of this the way
 * `attack.ts`'s `admitSwing` sits in front of `combat.ts`'s `resolveAttack`.
 * Putting them here would mean this module importing a skill tree, which is the
 * one thing the generic exists to avoid.
 *
 * A refusal is a returned result, never an exception: spending points is driven
 * by a player's click, and every other player-driven entry point in core
 * (`place`, `equip`, `quaff`) answers a bad request the same way. Only a
 * malformed *construction* throws.
 *
 * The spent record is **complete** — every key the ledger was opened with is
 * present at zero. That buys three things at once: an unknown key is detectable
 * (which is what refuses a fabricated skill id off the wire), the key order is
 * fixed so two clients serialise a ledger identically, and reading a key needs
 * no absence check. What goes on the wire is the schema layer's decision, not
 * this one's.
 */
const assertWholePoints = (what, value) => {
    // Negated so NaN is refused alongside the out-of-range values.
    if (!(Number.isInteger(value) && value >= 0)) {
        throw new RangeError(`${what} must be a whole non-negative number of points, received ${value}`);
    }
};
const isWholePoints = (value) => Number.isInteger(value) && value >= 0;
/** Reads one key without an absence check leaking into every call site. */
const held = (spent, key) => spent[key] ?? 0;
const zeroed = (keys) => Object.fromEntries(keys.map((key) => [key, 0]));
/**
 * Opens a ledger over a fixed set of keys.
 *
 * A duplicate key throws: two rows addressing one pot of points is a corrupt
 * table, not a game state, and it would make `refundPoints` hand back less than
 * was spent. An *empty* key set is legal and is the state a character is in
 * before their class has any skills — every spend against it is refused, which
 * is the correct answer to "spend a point on a skill that does not exist".
 */
export const createLedger = (keys, granted = 0) => {
    assertWholePoints('A grant', granted);
    if (new Set(keys).size !== keys.length) {
        throw new RangeError(`A ledger cannot hold the same key twice: ${keys.join(', ')}`);
    }
    return { granted, spent: zeroed(keys) };
};
/** The keys this ledger holds, in the order it was opened with. */
export const ledgerKeys = (ledger) => 
// `Object.keys` is typed `string[]` however narrow the record is. The record
// is built from `TKey`s by `zeroed` and nothing else can add a key to it.
Object.keys(ledger.spent);
/** What is standing on one key. Zero when nothing has been spent on it. */
export const spentOn = (ledger, key) => held(ledger.spent, key);
/** Every point currently committed, across every key. */
export const pointsSpent = (ledger) => ledgerKeys(ledger).reduce((total, key) => total + held(ledger.spent, key), 0);
/** What the character may still spend. Derived, never stored. */
export const pointsAvailable = (ledger) => ledger.granted - pointsSpent(ledger);
/**
 * Adds to the lifetime grant — what a level-up does, and the only thing that
 * ever raises `granted`.
 *
 * Throws rather than refusing, because the amount comes from a table constant
 * and never from a player: a fractional grant is a broken caller.
 */
export const grantPoints = (ledger, points) => {
    assertWholePoints('A grant', points);
    return points === 0 ? ledger : { ...ledger, granted: ledger.granted + points };
};
/**
 * Commits points to one key, or refuses the whole request.
 *
 * Never spends part of a request: asking for four points with three left buys
 * nothing at all, which is the same discipline `gold.ts`'s `spend` keeps for the
 * same reason — a half-applied purchase is a state no client could have
 * predicted.
 *
 * `key` is a plain `string` rather than `TKey` on purpose. This is the one
 * entry point a client's message reaches directly, so it has to be able to be
 * *wrong*; the ledger's own key set is what answers, and `'unknown-key'` is the
 * refusal a fabricated skill id earns.
 */
export const spendPoints = (ledger, key, points = 1) => {
    if (!isWholePoints(points))
        return { ok: false, reason: 'invalid-amount' };
    if (!Object.hasOwn(ledger.spent, key))
        return { ok: false, reason: 'unknown-key' };
    if (points === 0)
        return { ok: true, ledger, spent: 0 };
    if (points > pointsAvailable(ledger))
        return { ok: false, reason: 'insufficient-points' };
    const spent = { ...ledger.spent, [key]: held(ledger.spent, key) + points };
    return { ok: true, ledger: { ...ledger, spent }, spent: points };
};
/**
 * Empties every key and hands back exactly what was standing on them.
 *
 * The respec item's whole mechanism (`respec.ts`), and the reason it can be
 * proven exact: `granted` is untouched, so what a character may spend after a
 * refund is the number they were granted and cannot be anything else. A second
 * refund therefore hands back nothing — points cannot be laundered into new
 * ones by using two scrolls.
 */
export const refundPoints = (ledger) => {
    const refunded = pointsSpent(ledger);
    if (refunded === 0)
        return { ledger, refunded: 0 };
    return { ledger: { ...ledger, spent: zeroed(ledgerKeys(ledger)) }, refunded };
};
//# sourceMappingURL=points.js.map